import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Button } from '@/components'
import type { FileLabel } from '@/components/project/analysis-modal/types'
import { FileLabelSelector } from '@/components/project/FileLabelSelector'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { MAX_ARTIFACT_NAME_LENGTH } from '@/constants/artifacts'
import { useProductTourOptional } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import { TOUR_STEP } from '@/lib/product-tour/stepIndices'

const DEFAULT_LABEL: FileLabel = {}

// Invalid filename chars: < > : " / \ | ? *
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g

function sanitizeFilenameForPaste(name: string): string {
	return name
		.replace(INVALID_FILENAME_CHARS, '_')
		.replace(/^[\s.]+|[\s.]+$/g, '')
		.trim()
}

interface PasteTextModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (file: File, classification: FileLabel, artifactName: string) => void
	tourInitialText?: string
	tourInitialFilename?: string
	tourDefaultClassification?: FileLabel
}

export function PasteTextModal({
	open,
	onOpenChange,
	onSubmit,
	tourInitialText,
	tourInitialFilename,
	tourDefaultClassification,
}: PasteTextModalProps) {
	const { t } = useTranslation()
	const productTour = useProductTourOptional()
	const [text, setText] = useState('')
	const [filename, setFilename] = useState('')
	const [classification, setClassification] = useState<FileLabel>(DEFAULT_LABEL)
	const [showEmptyError, setShowEmptyError] = useState(false)
	const [filenameErrorType, setFilenameErrorType] = useState<'empty' | 'invalid_chars' | null>(null)
	const isTourLockedPasteStep =
		productTour?.isTourActive && productTour.stepIndex === TOUR_STEP.pasteModal && open

	const resetForm = useCallback(() => {
		setText('')
		setFilename('')
		setClassification(DEFAULT_LABEL)
		setShowEmptyError(false)
		setFilenameErrorType(null)
	}, [])

	useEffect(() => {
		if (!open) {
			resetForm()
		}
	}, [open, resetForm])

	useLayoutEffect(() => {
		if (!open) return
		if (tourInitialText === undefined || tourInitialText === '') return
		setText(tourInitialText)
		if (tourInitialFilename) setFilename(tourInitialFilename)
		if (tourDefaultClassification) setClassification(tourDefaultClassification)
	}, [open, tourInitialText, tourInitialFilename, tourDefaultClassification])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		const trimmedFilename = filename.trim()
		const trimmedText = text.trim()
		if (!trimmedFilename) {
			setFilenameErrorType('empty')
			return
		}
		if (!trimmedText) {
			setShowEmptyError(true)
			return
		}
		if (!classification.timeDimension) return
		// Strip .txt if user included it (case-insensitive), then sanitize and append .txt once
		const baseName = trimmedFilename.replace(/\.txt$/i, '')
		const sanitizedBaseName = sanitizeFilenameForPaste(baseName)
		if (!sanitizedBaseName) {
			setFilenameErrorType('invalid_chars')
			return
		}
		const nameWithExt = `${sanitizedBaseName}.txt`
		const file = new File([trimmedText], nameWithExt, { type: 'text/plain' })
		const artifactName = nameWithExt.slice(0, MAX_ARTIFACT_NAME_LENGTH)
		onSubmit(file, classification, artifactName)
		onOpenChange(false)
		resetForm()
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="flex max-h-[90vh] flex-col gap-4 overflow-hidden sm:max-w-lg"
				data-tour={TOUR.pasteModal}
			>
				<DialogHeader className="shrink-0">
					<DialogTitle>{t('pages.projectDetail.filesCard.pasteTextModal.title')}</DialogTitle>
					<DialogDescription>
						{t('pages.projectDetail.filesCard.pasteTextModal.description')}
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
					<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden pr-1">
						<div className="flex flex-col gap-2">
							<Label htmlFor="paste-filename">
								{t('pages.projectDetail.filesCard.pasteTextModal.filenameLabel')}
							</Label>
							<Input
								id="paste-filename"
								type="text"
								value={filename}
								onChange={(e) => {
									setFilename(e.target.value)
									setFilenameErrorType(null)
								}}
								className="w-full"
								aria-invalid={!!filenameErrorType}
								aria-describedby={filenameErrorType ? 'paste-filename-error' : undefined}
								disabled={isTourLockedPasteStep}
							/>
							{filenameErrorType && (
								<p id="paste-filename-error" className="text-sm text-destructive">
									{t(
										filenameErrorType === 'empty'
											? 'pages.projectDetail.filesCard.pasteTextModal.filenameError'
											: 'pages.projectDetail.filesCard.pasteTextModal.filenameInvalidCharsError'
									)}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<Label htmlFor="paste-textarea">
								{t('pages.projectDetail.filesCard.pasteTextModal.textareaLabel')}
							</Label>
							<Textarea
								id="paste-textarea"
								data-tour={TOUR.pasteTextarea}
								value={text}
								onChange={(e) => {
									setText(e.target.value)
									setShowEmptyError(false)
								}}
								placeholder={t('pages.projectDetail.filesCard.pasteTextModal.textareaPlaceholder')}
								className="field-sizing-fixed min-h-32 max-h-48 overflow-y-auto"
								aria-invalid={showEmptyError}
								aria-describedby={showEmptyError ? 'paste-empty-error' : undefined}
								disabled={isTourLockedPasteStep}
							/>
							{showEmptyError && (
								<p id="paste-empty-error" className="text-sm text-destructive">
									{t('pages.projectDetail.filesCard.pasteTextModal.emptyError')}
								</p>
							)}
						</div>

						<FileLabelSelector
							label={classification}
							onChange={setClassification}
							isContextFile
							disabled={isTourLockedPasteStep}
							disableTooltips={isTourLockedPasteStep}
						/>
					</div>

					<DialogFooter className="shrink-0 border-t border-border pt-4">
						<Button type="button" isWhite={true} onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button
							type="submit"
							isWhite={false}
							disabled={!classification.timeDimension}
							data-tour={TOUR.pasteSubmit}
						>
							{t('pages.projectDetail.filesCard.pasteTextModal.submit')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
